import axios from 'axios'
import styles from './stats.module.css'
import { useEffect, useState } from 'react'
import { Doughnut, Bar } from 'react-chartjs-2'
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
} from 'chart.js'

// Register ChartJS components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
)

const Stats = () => {
    const [data, setData] = useState([]);
    const [topCabs, setTopCabs] = useState([]);
    const [timePeriodData, setTimePeriodData] = useState(null);
    const [timePeriodLoading, setTimePeriodLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [isPie, setIsPie] = useState(true);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        startDate: '',
        endDate: ''
    });
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    }

    // Calculate total days between dates
    const calculateTotalDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;

        const start = new Date(startDate);
        const end = new Date(endDate);
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

        return daysDiff;
    }

    const fetchData = async (startDate = null, endDate = null) => {
        try {
            setLoading(true);

            // Build query parameters
            const queryParams = new URLSearchParams();
            if (startDate) queryParams.append('startDate', startDate);
            if (endDate) queryParams.append('endDate', endDate);

            console.log('Fetching data with params:', queryParams.toString());

            const queryString = queryParams.toString();
            const url = `${process.env.REACT_APP_URL}/api/rides/rideStats${queryString ? `?${queryString}` : ''}`;

            const response = await axios.get(url, { withCredentials: true });
            setData(response?.data?.rideStatistics || []);
            setTopCabs(response?.data?.topCabs || []);
            console.log("Ride statistics data:", response.data);

            // Set filter applied state
            setIsFilterApplied(!!startDate);
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false);
        }
    }

    const fetchTimePeriodData = async (startDate = null, endDate = null) => {
        try {
            setTimePeriodLoading(true);

            // Build query parameters
            const queryParams = new URLSearchParams();
            if (startDate) queryParams.append('startDate', startDate);
            if (endDate) queryParams.append('endDate', endDate);

            const queryString = queryParams.toString();
            const url = `${process.env.REACT_APP_URL}/api/rides/rideStats55${queryString ? `?${queryString}` : ''}`;

            const res = await axios.get(url, { withCredentials: true });
            setTimePeriodData(res.data);
            // Auto-select first period
            if (res.data.timePeriodStats?.length > 0) {
                setSelectedPeriod(res.data.timePeriodStats[0]);
            }
        } catch (err) {
            console.error('Error:', err);
        } finally {
            setTimePeriodLoading(false);
        }
    }

    const validateDates = () => {
        const newErrors = {
            startDate: '',
            endDate: ''
        };

        if (dateRange.startDate && dateRange.endDate) {
            if (dateRange.startDate === dateRange.endDate) {
                newErrors.endDate = 'End date cannot be the same as start date';
            } else if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
                newErrors.endDate = 'End date cannot be before start date';
            }
        }

        setErrors(newErrors);
        return !newErrors.startDate && !newErrors.endDate;
    }

    const handleDateChange = (e) => {
        const { name, value } = e.target;

        // Clear errors when user starts typing
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));

        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    }

    const handleFilter = () => {
        if (!validateDates()) {
            return;
        }

        // If only start date is provided, fetch data for that single day
        // If both dates are provided, fetch data for the range
        fetchData(dateRange.startDate || null, dateRange.endDate || null);
        fetchTimePeriodData(dateRange.startDate || null, dateRange.endDate || null);
    }

    const handleReset = () => {
        setDateRange({
            startDate: '',
            endDate: ''
        });
        setErrors({
            startDate: '',
            endDate: ''
        });
        setIsFilterApplied(false);
        fetchData(); // Fetch without any date filters
        fetchTimePeriodData(); // Fetch without any date filters
    }

    // Auto-validate when dates change
    useEffect(() => {
        if (dateRange.startDate || dateRange.endDate) {
            validateDates();
        }
    }, [dateRange.startDate, dateRange.endDate]);

    const formatStatus = (status) => {
        if (status === null) return 'Unknown';
        if (status === 'TOTAL') return 'Total Rides';

        const statusMap = {
            'noResponse': 'No Response',
            'notReached': 'Not Reached',
            'completed': 'Completed',
            'cancelled_driver': 'Cancelled by Driver',
            'cancelled_rider': 'Cancelled by Rider',
            'onGoing': 'On Going'
        };

        return statusMap[status] || status;
    }

    // Filter out TOTAL and null for charts
    const chartData = data.filter(item => item.status !== 'TOTAL' && item.status !== null);

    // Calculate percentages
    const total = data.find(item => item.status === 'TOTAL')?.TotalCount || 1;

    // Get top 5 drivers
    const topDrivers = topCabs.slice(0, 5);

    // Enhanced color palette with better distinction
    const chartColors = {
        notReached: '#e74c3c',
        completed: '#27ae60',
        cancelled_driver: '#f39c12',
        noResponse: '#3498db',
        cancelled_rider: '#e67e22',
        onGoing: '#9b59b6',
        nullStatus: '#95a5a6'
    };

    // Border colors for bar chart
    const borderColors = {
        notReached: '#c0392b',
        completed: '#229954',
        cancelled_driver: '#e67e22',
        noResponse: '#2980b9',
        cancelled_rider: '#d35400',
        onGoing: '#8e44ad',
        nullStatus: '#7f8c8d'
    };

    // Pie Chart Data
    const pieChartData = {
        labels: chartData.map(item => formatStatus(item.status)),
        datasets: [
            {
                data: chartData.map(item => item.TotalCount),
                backgroundColor: chartData.map(item => chartColors[item.status] || chartColors.nullStatus),
                borderColor: '#fff',
                borderWidth: 3,
                hoverBorderWidth: 4,
            },
        ],
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '50%',
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: {
                        size: 11,
                        weight: '600'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#2c3e50',
                bodyColor: '#2c3e50',
                borderColor: '#e9ecef',
                borderWidth: 1,
                cornerRadius: 8,
                callbacks: {
                    label: function (context) {
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${value} rides (${percentage}%)`;
                    }
                }
            }
        },
    };

    // Bar Chart Data for Ride Status
    const barChartData = {
        labels: chartData.map(item => formatStatus(item.status)),
        datasets: [
            {
                label: 'Number of Rides',
                data: chartData.map(item => item.TotalCount),
                backgroundColor: chartData.map(item => chartColors[item.status] || chartColors.nullStatus),
                borderColor: chartData.map(item => borderColors[item.status] || borderColors.nullStatus),
                borderWidth: 2,
                borderRadius: 6,
                borderSkipped: false,
            },
        ],
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Rides by Status',
                font: {
                    size: 16,
                    weight: '600'
                },
                color: '#2c3e50'
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#2c3e50',
                bodyColor: '#2c3e50',
                borderColor: '#e9ecef',
                borderWidth: 1,
                cornerRadius: 8,
                callbacks: {
                    label: function (context) {
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${value} rides (${percentage}%)`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                title: {
                    display: true,
                    text: 'Number of Rides',
                    color: '#6c757d',
                    font: {
                        weight: '600'
                    }
                },
                ticks: {
                    color: '#6c757d'
                }
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Ride Status',
                    color: '#6c757d',
                    font: {
                        weight: '600'
                    }
                },
                ticks: {
                    color: '#6c757d',
                    maxRotation: 45
                }
            }
        },
    };

    // Bar Chart Data for Top Drivers
    const topDriversChartData = {
        labels: topDrivers.map(driver => driver.driver_name),
        datasets: [
            {
                label: 'Number of Rides',
                data: topDrivers.map(driver => driver.TotalCount),
                backgroundColor: [
                    '#FFD700', // Gold for 1st
                    '#C0C0C0', // Silver for 2nd
                    '#CD7F32', // Bronze for 3rd
                    '#3498db', // Blue for 4th
                    '#27ae60'  // Green for 5th
                ],
                borderColor: [
                    '#FFC400',
                    '#A8A8A8',
                    '#B08D57',
                    '#2980b9',
                    '#229954'
                ],
                borderWidth: 2,
                borderRadius: 6,
                borderSkipped: false,
            },
        ],
    };

    const topDriversChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Top 5 Drivers',
                font: {
                    size: 16,
                    weight: '600'
                },
                color: '#2c3e50'
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#2c3e50',
                bodyColor: '#2c3e50',
                borderColor: '#e9ecef',
                borderWidth: 1,
                cornerRadius: 8,
                callbacks: {
                    label: function (context) {
                        const driver = topDrivers[context.dataIndex];
                        return [
                            `Driver: ${driver.driver_name}`,
                            `Rides: ${driver.TotalCount}`,
                            `Cab ID: ${driver.cab_id}`
                        ];
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                title: {
                    display: true,
                    text: 'Number of Rides',
                    color: '#6c757d',
                    font: {
                        weight: '600'
                    }
                },
                ticks: {
                    color: '#6c757d'
                }
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Driver Name',
                    color: '#6c757d',
                    font: {
                        weight: '600'
                    }
                },
                ticks: {
                    color: '#6c757d',
                    maxRotation: 45
                }
            }
        },
    };

    useEffect(() => {
        fetchData();
        fetchTimePeriodData();
    }, [])

    return (
        <div className={styles.container}>
            <h3 className={styles.head}>Ride Statistics Dashboard</h3>

            <div className={styles.filterSection}>
                <div className={styles.dateFilters}>
                    <div className={styles.dateInputGroup}>
                        <label htmlFor="startDate">From Date *</label>
                        <input
                            type="date"
                            id="startDate"
                            name="startDate"
                            value={dateRange.startDate}
                            onChange={handleDateChange}
                            className={`${styles.dateInput} ${errors.startDate ? styles.error : ''}`}
                            max={getTodayDate()}
                        />
                        {errors.startDate && (
                            <div className={styles.errorText}>{errors.startDate}</div>
                        )}
                    </div>
                    <div className={styles.dateInputGroup}>
                        <label htmlFor="endDate">To Date (Optional)</label>
                        <input
                            type="date"
                            id="endDate"
                            name="endDate"
                            value={dateRange.endDate}
                            onChange={handleDateChange}
                            className={`${styles.dateInput} ${errors.endDate ? styles.error : ''}`}
                            max={getTodayDate()}
                            min={dateRange.startDate ? new Date(new Date(dateRange.startDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : ''}
                        />
                    </div>
                    <div className={styles.filterButtons}>
                        <button
                            onClick={handleFilter}
                            disabled={loading || !dateRange.startDate || Object.values(errors).some(error => error)}
                            className={styles.filterButton}
                        >
                            {loading ? 'Loading...' : 'Apply Filter'}
                        </button>
                        <button
                            onClick={handleReset}
                            disabled={loading}
                            className={styles.resetButton}
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Date Range Display - Blue Container */}
            {isFilterApplied && (
                <div className={styles.dateRangeContainer}>
                    <div className={styles.dateRangeContent}>
                        <span className={styles.dateRangeText}>
                            From <strong>{dateRange.startDate}</strong>
                            {dateRange.endDate && (
                                <> to <strong>{dateRange.endDate}</strong></>
                            )}
                            {dateRange.endDate && (
                                <span className={styles.totalDays}>
                                    ({calculateTotalDays(dateRange.startDate, dateRange.endDate)} days)
                                </span>
                            )}
                            {!dateRange.endDate && (
                                <span className={styles.totalDays}>(Single day)</span>
                            )}
                        </span>
                    </div>
                </div>
            )}

            {/* Charts Section */}
            <div className={styles.dashboard}>
                <div className={styles.chartContainer}>
                    <button
                        className={`${styles.toggleButton} ${styles.active}`}
                        onClick={() => setIsPie((prev) => !prev)}>
                        {isPie ? 'To Graph' : 'To Pie Chart'}
                    </button>

                    <div style={{ height: '270px', width: '100%' }}>
                        {isPie ?
                            <Doughnut data={pieChartData} options={pieChartOptions} /> :
                            <Bar data={barChartData} options={barChartOptions} />
                        }
                    </div>
                </div>
                <div className={styles.chartContainer}>
                    <div style={{ height: '270px', width: '100%' }}>
                        {topDrivers.length > 0 ? (
                            <Bar data={topDriversChartData} options={topDriversChartOptions} />
                        ) : (
                            <div className={styles.noData}>
                                <p>No driver data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Time Period Chart - DIRECTLY IN THE STATS COMPONENT */}


            {/* Stats Cards Grid */}
            <div className={styles.statsGrid}>
                {data.map((item, index) => {
                    const percentage = item.status !== 'TOTAL' && item.status !== null
                        ? ((item.TotalCount / total) * 100).toFixed(1)
                        : null;

                    return (
                        <div
                            className={`${styles.statCard} ${styles[item.status || 'nullStatus']}`}
                            key={index}
                        >
                            <div className={styles.status}>
                                {formatStatus(item.status)}
                            </div>
                            <div className={styles.count}>
                                {item.TotalCount}
                            </div>
                            {percentage && (
                                <div className={styles.percentage}>
                                    {percentage}% of total
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            <div className={styles.timePeriodSection}>
                {timePeriodLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner">Loading trip statistics...</div>
                    </div>
                ) : !timePeriodData ? (
                    <div className="error-container">
                        <div>No time period data available</div>
                    </div>
                ) : (
                    <div className="time-period-dashboard">
                        {/* Header */}
                        <div className="dashboard-header">
                            <h1>Trip Distribution by Time of Day</h1>
                            <div className="total-trips">
                                Total Trips: <span className="total-count">{timePeriodData.totalTrips}</span>
                            </div>
                        </div>

                        {/* Main Content Grid */}
                        <div className="dashboard-content">
                            {/* Left Panel - Chart and Overview */}
                            <div className="left-panel">
                                <div className="chart-container">
                                    <div className="bar-chart-container">
                                        {timePeriodData.timePeriodStats.map(period => {
                                            const maxTrips = Math.max(...timePeriodData.timePeriodStats.map(p => p.totalTrips));
                                            return (
                                                <div
                                                    key={period.period}
                                                    className={`bar-chart-item ${selectedPeriod?.period === period.period ? 'active' : ''}`}
                                                    onClick={() => setSelectedPeriod(period)}
                                                >
                                                    <div className="bar-wrapper">
                                                        <div
                                                            className="bar"
                                                            style={{
                                                                height: `${(period.totalTrips / maxTrips) * 80}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="bar-label">
                                                        <div className="period-name">{period.label.split(' ')[0]}</div>
                                                        <div className="trip-count">{period.totalTrips}</div>
                                                        <div className="percentage">
                                                            {((period.totalTrips / timePeriodData.totalTrips) * 100).toFixed(1)}%
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Period Cards */}
                                <div className="period-cards">
                                    {timePeriodData.timePeriodStats.map(period => (
                                        <div
                                            key={period.period}
                                            className={`period-card ${selectedPeriod?.period === period.period ? 'active' : ''}`}
                                            onClick={() => setSelectedPeriod(period)}
                                        >
                                            <div className="card-header">
                                                <h3>{period.label}</h3>
                                                <div className="time-range">
                                                    {timePeriodData.timePeriodDefinitions[period.period].start}:00 -
                                                    {timePeriodData.timePeriodDefinitions[period.period].end === 5 ? '04:59' : `${timePeriodData.timePeriodDefinitions[period.period].end}:59`}
                                                </div>
                                            </div>
                                            <div className="card-stats">
                                                <div className="stat-main">{period.totalTrips}</div>
                                                <div className="stat-sub">
                                                    {period.uniqueGeohashes} locations • {((period.totalTrips / timePeriodData.totalTrips) * 100).toFixed(1)}%
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Panel - Detailed View */}
                            <div className="right-panel">
                                {selectedPeriod && (
                                    <div className="detail-panel">
                                        <div className="detail-header">
                                            <h2>{selectedPeriod.label}</h2>
                                            <div className="detail-stats">
                                                <span className="stat">{selectedPeriod.totalTrips} total trips</span>
                                                <span className="stat">{selectedPeriod.uniqueGeohashes} locations</span>
                                                <span className="stat">{((selectedPeriod.totalTrips / timePeriodData.totalTrips) * 100).toFixed(1)}% of all trips</span>
                                            </div>
                                        </div>

                                        {/* Top Locations */}
                                        <div className="locations-section">
                                            <h3>Top Locations</h3>
                                            <div className="locations-list">
                                                {selectedPeriod.geohashes.slice(0, 10).map((location, index) => (
                                                    <div key={location.geohash} className="location-item">
                                                        <div className="location-rank">#{index + 1}</div>
                                                        <div className="location-details">
                                                            <div className="geohash">{location.geohash}</div>
                                                            <div className="location-stats">
                                                                <span className="count">{location.count} trips</span>
                                                                <span className="percent">
                                                                    {((location.count / selectedPeriod.totalTrips) * 100).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {selectedPeriod.geohashes.length === 0 && (
                                                    <div className="no-data">No trip data for this period</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Time Period Info */}
                                        <div className="period-info">
                                            <h4>Time Period Information</h4>
                                            <div className="info-grid">
                                                <div className="info-item">
                                                    <label>Start Time:</label>
                                                    <span>{timePeriodData.timePeriodDefinitions[selectedPeriod.period].start}:00</span>
                                                </div>
                                                <div className="info-item">
                                                    <label>End Time:</label>
                                                    <span>
                                                        {timePeriodData.timePeriodDefinitions[selectedPeriod.period].end === 5 ?
                                                            '04:59 (next day)' :
                                                            `${timePeriodData.timePeriodDefinitions[selectedPeriod.period].end}:59`
                                                        }
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <label>Duration:</label>
                                                    <span>
                                                        {timePeriodData.timePeriodDefinitions[selectedPeriod.period].end > timePeriodData.timePeriodDefinitions[selectedPeriod.period].start ?
                                                            `${timePeriodData.timePeriodDefinitions[selectedPeriod.period].end - timePeriodData.timePeriodDefinitions[selectedPeriod.period].start} hours` :
                                                            'Overnight (crosses midnight)'
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Stats